import React, { useEffect, useState } from 'react';
import { achievementAPI, companyAPI } from '../services/api';
import Layout from '../components/Layout';
import Icon from '../components/Icon';

// Tier colors for dark theme
const TIER_COLORS = {
  bronze: { bg: 'linear-gradient(135deg, #cd7f32 0%, #8b4513 100%)', text: 'white' },
  silver: { bg: 'linear-gradient(135deg, #c0c0c0 0%, #808080 100%)', text: '#1a1a1a' },
  gold: { bg: 'linear-gradient(135deg, #ffd700 0%, #ff8c00 100%)', text: '#1a1a1a' },
  platinum: { bg: 'linear-gradient(135deg, #e5e4e2 0%, #9090a0 100%)', text: '#1a1a1a' },
};

const CATEGORY_ICONS = {
  bikes: 'bike',
  services: 'service',
  customers: 'users',
  reviews: 'star',
  bookings: 'calendar',
  other: 'achievement',
};

const buildStatsFromProgress = (progressData = {}, templates = [], achievements = []) => {
  const unlocked = typeof progressData.unlocked_count === 'number'
    ? progressData.unlocked_count
    : achievements.filter((entry) => entry.is_unlocked).length;

  const total = typeof progressData.total === 'number'
    ? progressData.total
    : (templates || []).length;

  const totalXP = typeof progressData.total_xp === 'number'
    ? progressData.total_xp
    : achievements.reduce((sum, entry) => {
      if (!entry.is_unlocked || !entry.template) return sum;
      return sum + Number(entry.template.xp_reward || 0);
    }, 0);

  return {
    unlocked,
    total,
    totalXP,
    hasAllAchievements: Boolean(progressData.has_all_achievements),
  };
};

const getXpMilestones = (templates = []) => (
  (templates || [])
    .filter((template) => template?.category === 'xp' && Number(template.threshold || 0) > 0)
    .sort((a, b) => Number(a.threshold || 0) - Number(b.threshold || 0))
);

const getXpTrackInfo = (totalXP, templates) => {
  const milestones = getXpMilestones(templates);
  const baseMilestone = { id: 'xp_start', name: 'Start', threshold: 0 };
  if (!milestones.length) {
    return {
      milestones: [],
      currentMilestone: baseMilestone,
      nextMilestone: null,
      levelProgress: 0,
      remainingXP: 0,
    };
  }

  const currentMilestone = milestones
    .filter((milestone) => totalXP >= Number(milestone.threshold || 0))
    .pop() || baseMilestone;
  const nextMilestone = milestones.find((milestone) => totalXP < Number(milestone.threshold || 0)) || null;

  const previousThreshold = currentMilestone ? Number(currentMilestone.threshold || 0) : 0;
  const nextThreshold = nextMilestone ? Number(nextMilestone.threshold || 0) : previousThreshold;
  const span = Math.max(nextThreshold - previousThreshold, 1);
  const progressInSpan = nextMilestone ? Math.max(totalXP - previousThreshold, 0) : span;
  const levelProgress = Math.min((progressInSpan / span) * 100, 100);
  const remainingXP = nextMilestone ? Math.max(nextThreshold - totalXP, 0) : 0;

  return {
    milestones,
    currentMilestone,
    nextMilestone,
    levelProgress,
    remainingXP,
  };
};

function AchievementCard({ achievement, template, compact = false }) {
  const isUnlocked = achievement?.is_unlocked;
  const progress = achievement?.progress || 0;
  const tier = template?.tier || 'bronze';
  const tierStyle = TIER_COLORS[tier] || TIER_COLORS.bronze;
  const threshold = template?.threshold || 1;
  const progressPercent = Math.min((progress / threshold) * 100, 100);

  return (
    <div 
      style={{
        background: isUnlocked ? tierStyle.bg : 'rgba(255, 255, 255, 0.03)',
        border: isUnlocked ? 'none' : '1px solid rgba(255, 255, 255, 0.06)',
        borderRadius: compact ? '12px' : '16px',
        padding: compact ? '14px 12px' : '20px',
        boxShadow: isUnlocked ? '0 8px 32px rgba(0,0,0,0.3)' : 'none',
        transition: 'all 0.3s ease',
        opacity: isUnlocked ? 1 : 0.7,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {!isUnlocked && (
        <div style={{
          position: 'absolute',
          top: compact ? '8px' : '12px',
          right: compact ? '8px' : '12px',
          fontSize: compact ? '14px' : '18px',
          opacity: 0.4,
        }}>
          🔒
        </div>
      )}

      <div style={{
        fontSize: compact ? '28px' : '42px',
        textAlign: 'center',
        marginBottom: compact ? '8px' : '12px',
        filter: isUnlocked ? 'none' : 'grayscale(100%)',
      }}>
        {template?.iconName ? (
          <Icon name={template.iconName} color={isUnlocked ? "primary" : "muted"} darkMode={true} size={compact ? 28 : 42} />
        ) : (
          <Icon name="achievement" color={isUnlocked ? "primary" : "muted"} darkMode={true} size={compact ? 28 : 42} />
        )}
      </div>

      <h3 style={{
        margin: '0 0 6px 0',
        fontSize: compact ? '12px' : '15px',
        fontWeight: 'bold',
        textAlign: 'center',
        color: isUnlocked ? tierStyle.text : 'rgba(255,255,255,0.7)',
        lineHeight: 1.3,
      }}>
        {template?.name || 'Achievement'}
      </h3>

      {!compact && (
        <p style={{
          margin: '0 0 14px 0',
          fontSize: '12px',
          textAlign: 'center',
          color: isUnlocked ? tierStyle.text : 'rgba(255,255,255,0.5)',
          opacity: isUnlocked ? 0.85 : 1,
        }}>
          {template?.description || ''}
        </p>
      )}

      <div style={{
        background: 'rgba(0,0,0,0.2)',
        borderRadius: '10px',
        height: compact ? '4px' : '6px',
        overflow: 'hidden',
        marginBottom: compact ? '6px' : '8px',
      }}>
        <div style={{
          background: isUnlocked ? 'rgba(255,255,255,0.9)' : 'var(--lime-primary)',
          height: '100%',
          width: `${progressPercent}%`,
          borderRadius: '10px',
          transition: 'width 0.5s ease',
        }} />
      </div>

      <div style={{
        fontSize: compact ? '10px' : '11px',
        textAlign: 'center',
        color: isUnlocked ? tierStyle.text : 'rgba(255,255,255,0.5)',
        fontWeight: '600',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
          {isUnlocked ? (
            <>
              <Icon name="success" color={isUnlocked ? tierStyle.text : "muted"} darkMode={true} size={compact ? 10 : 12} />
              Unlocked!
            </>
          ) : (
            `${progress} / ${threshold}`
          )}
        </span>
      </div>

      {/* What happens when unlocked: XP + reward */}
      <div style={{
        marginTop: compact ? '6px' : '10px',
        padding: compact ? '6px 8px' : '8px 10px',
        background: isUnlocked ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.04)',
        borderRadius: '8px',
        fontSize: compact ? '10px' : '11px',
        color: isUnlocked ? tierStyle.text : 'rgba(255,255,255,0.6)',
        lineHeight: 1.4,
      }}>
        {template?.xp_reward != null && template.xp_reward > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: template?.reward_description ? '4px' : 0 }}>
            <Icon name="star" color={isUnlocked ? "primary" : "muted"} darkMode={true} size={compact ? 10 : 12} />
            <span>+{template.xp_reward} XP{isUnlocked ? ' earned' : ''}</span>
          </div>
        )}
        {template?.reward_description && !compact && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
            <Icon name="tag" color={isUnlocked ? "primary" : "muted"} darkMode={true} size={compact ? 10 : 12} style={{ marginTop: '1px', flexShrink: 0 }} />
            <span>{template.reward_description}</span>
          </div>
        )}
        {(!template?.xp_reward || template.xp_reward === 0) && !template?.reward_description && (
          <span style={{ opacity: 0.7 }}>Unlock to earn rewards</span>
        )}
      </div>

      <div style={{
        position: 'absolute',
        top: compact ? '8px' : '12px',
        left: compact ? '8px' : '12px',
        fontSize: compact ? '8px' : '9px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        padding: compact ? '2px 6px' : '4px 8px',
        borderRadius: '10px',
        background: isUnlocked ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.05)',
        color: isUnlocked ? tierStyle.text : 'rgba(255,255,255,0.4)',
        letterSpacing: '0.5px',
      }}>
        {tier}
      </div>
    </div>
  );
}

function CompanyAchievementsPage({ user, onLogout, darkMode = true, onToggleDarkMode }) {
  const [achievements, setAchievements] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState({ unlocked: 0, total: 0, totalXP: 0, hasAllAchievements: false });
  const [upcomingRewards, setUpcomingRewards] = useState([]);
  const [claimedRewards, setClaimedRewards] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 480);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const companyResp = await companyAPI.getCompanyInfo();
      setCompany(companyResp.data.company);
      
      const [tmplResp, progressResp] = await Promise.all([
        achievementAPI.getTemplates(),
        achievementAPI.getCompanyProgress(companyResp.data.company.id),
      ]);
      
      const templatesData = tmplResp.data.templates || [];
      const achievementsData = progressResp.data.achievements || [];

      setTemplates(templatesData);
      setAchievements(achievementsData);
      setUpcomingRewards(progressResp.data.upcoming_rewards || []);
      setClaimedRewards(progressResp.data.claimed_rewards || []);

      setStats(buildStatsFromProgress(progressResp.data, templatesData, achievementsData));
      
    } catch (err) {
      console.error('Error loading achievements:', err);
      setError(err.response?.data?.error || 'Failed to load achievements');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!company) return;
    
    try {
      setRefreshing(true);
      setError('');
      setSuccess('');
      
      const response = await achievementAPI.refreshAchievements(company.id);
      await fetchData();
      
      // Show success message with newly unlocked achievements
      if (response.data.newly_unlocked && response.data.newly_unlocked.length > 0) {
        setSuccess(`New achievements unlocked: ${response.data.newly_unlocked.join(', ')}`);
      } else {
        setSuccess('Achievements refreshed successfully!');
      }
      
      setTimeout(() => setSuccess(''), 5000);
      
    } catch (err) {
      console.error('Error refreshing achievements:', err);
      setError(err.response?.data?.error || 'Failed to refresh achievements');
    } finally {
      setRefreshing(false);
    }
  };

  const categories = ['all', ...new Set(templates.map(t => t.category).filter(Boolean))];
  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  const achievementMap = {};
  achievements.forEach(a => {
    achievementMap[a.achievement_template_id] = a;
  });

  return (
    <Layout 
      user={user} 
      onLogout={onLogout}
      pageTitle="Achievements"
      pageSubtitle={company ? company.name : 'Company achievements'}
     darkMode={darkMode} onToggleDarkMode={onToggleDarkMode}>
      {/* Refresh Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button 
          onClick={handleRefresh}
          disabled={refreshing || loading}
          className="btn btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          {refreshing ? (
            <>
              <Icon name="loading" color="primary" darkMode={darkMode} size={16} />
              Refreshing...
            </>
          ) : (
            <>
              <Icon name="refresh" color="primary" darkMode={darkMode} size={16} />
              Refresh Progress
            </>
          )}
        </button>
      </div>

      {error && <div className="error" style={{marginBottom: '20px'}}>{error}</div>}
      {success && (
        <div className="success" style={{
          marginBottom: '20px',
          padding: '16px',
          background: 'rgba(16, 185, 129, 0.15)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '12px',
          color: '#4A8C18'
        }}>
          {success}
        </div>
      )}

      {/* 10% Discount Banner - Shows when all achievements are unlocked */}
      {stats.hasAllAchievements && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(61, 90, 32, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)',
          border: '2px solid #3d5a20',
          borderRadius: isMobile ? '14px' : '20px',
          padding: isMobile ? '16px' : '24px 32px',
          marginBottom: isMobile ? '16px' : '24px',
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '12px' : '20px',
          flexWrap: 'wrap',
          boxShadow: 'var(--shadow-glow)',
          animation: 'discountPulse 2s ease-in-out infinite'
        }}>
          <Icon name="celebration" color="success" darkMode={darkMode} size={isMobile ? 32 : 48} />
          <div style={{ flex: 1, minWidth: isMobile ? '140px' : '200px' }}>
            <h3 style={{ 
              margin: '0 0 6px 0', 
              fontSize: isMobile ? '15px' : '20px', 
              color: '#3d5a20',
              fontWeight: '700'
            }}>
              Gratulálunk! Minden achievement teljesítve!
            </h3>
            <p style={{ 
              margin: 0, 
              fontSize: isMobile ? '13px' : '15px', 
              color: 'rgba(255,255,255,0.8)'
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <Icon name="tag" color="brand" darkMode={darkMode} size={isMobile ? 14 : 18} />
                <strong style={{ color: '#1A2811' }}>10% discount</strong> on all service bookings!
              </span> 
              Mention this when making your booking.
            </p>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #1A2811 0%, #2a4019 100%)',
            color: '#ffffff',
            padding: isMobile ? '8px 16px' : '12px 24px',
            borderRadius: '12px',
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: '800',
            boxShadow: 'var(--shadow-glow-active)'
          }}>
            -10%
          </div>
        </div>
      )}

      {/* Stats Header */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
        border: '1px solid rgba(102, 126, 234, 0.3)',
        borderRadius: '20px',
        padding: isMobile ? '20px 16px' : '32px',
        marginBottom: isMobile ? '20px' : '32px',
        display: 'flex',
        justifyContent: 'center',
        gap: isMobile ? '16px' : '48px',
        flexWrap: 'wrap'
      }}>
        <div style={{textAlign: 'center', minWidth: isMobile ? '60px' : 'auto'}}>
          <div style={{ fontSize: isMobile ? '28px' : '42px', fontWeight: 'bold', color: '#ffffff' }}>{stats.unlocked}</div>
          <div style={{ fontSize: isMobile ? '11px' : '13px', color: 'rgba(255,255,255,0.6)' }}>Unlocked</div>
        </div>
        <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)', alignSelf: 'stretch' }} />
        <div style={{textAlign: 'center', minWidth: isMobile ? '60px' : 'auto'}}>
          <div style={{ fontSize: isMobile ? '28px' : '42px', fontWeight: 'bold', color: '#ffffff' }}>{stats.total}</div>
          <div style={{ fontSize: isMobile ? '11px' : '13px', color: 'rgba(255,255,255,0.6)' }}>Total</div>
        </div>
        <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)', alignSelf: 'stretch' }} />
        <div style={{textAlign: 'center', minWidth: isMobile ? '60px' : 'auto'}}>
          <div style={{ fontSize: isMobile ? '28px' : '42px', fontWeight: 'bold', color: '#ffffff' }}>
            {stats.total > 0 ? Math.round((stats.unlocked / stats.total) * 100) : 0}%
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '13px', color: 'rgba(255,255,255,0.6)' }}>Complete</div>
        </div>
        <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)', alignSelf: 'stretch' }} />
        <div style={{textAlign: 'center', minWidth: isMobile ? '60px' : 'auto'}}>
          <div style={{ fontSize: isMobile ? '28px' : '42px', fontWeight: 'bold', color: '#ffd700' }}>
            {stats.totalXP}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '13px', color: 'rgba(255,255,255,0.6)' }}>Total XP</div>
        </div>
      </div>

      {/* XP Level Progress */}
      {(() => {
        const xpTrack = getXpTrackInfo(stats.totalXP || 0, templates);
        const milestones = xpTrack.milestones;
        if (!milestones.length) return null;

        const currentMilestone = xpTrack.currentMilestone || milestones[0];
        const nextMilestone = xpTrack.nextMilestone;

        return (
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '16px',
            padding: isMobile ? '16px' : '24px',
            marginBottom: isMobile ? '20px' : '32px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: isMobile ? '36px' : '44px',
                  height: isMobile ? '36px' : '44px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #4A8C18, #3d5a20)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '16px' : '20px',
                  fontWeight: '800',
                  color: '#fff',
                  boxShadow: '0 0 20px rgba(74, 140, 24, 0.35)'
                }}>
                  XP
                </div>
                <div>
                  <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', color: '#4A8C18' }}>
                    XP Track
                  </div>
                  <div style={{ fontSize: isMobile ? '11px' : '12px', color: 'rgba(255,255,255,0.5)' }}>
                    Current checkpoint: {Number(currentMilestone.threshold || 0)} XP
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '800', color: '#ffd700' }}>
                  {stats.totalXP} XP
                </div>
                {nextMilestone && (
                  <div style={{ fontSize: isMobile ? '11px' : '12px', color: 'rgba(255,255,255,0.5)' }}>
                    {xpTrack.remainingXP} XP to next reward checkpoint
                  </div>
                )}
              </div>
            </div>

            {/* XP Progress bar */}
            <div style={{
              background: 'rgba(255,255,255,0.06)',
              borderRadius: '10px',
              height: isMobile ? '10px' : '12px',
              overflow: 'hidden',
              marginBottom: '14px'
            }}>
              <div style={{
                background: 'linear-gradient(90deg, #3d5a20, #84cc16)',
                height: '100%',
                width: `${xpTrack.levelProgress}%`,
                borderRadius: '10px',
                transition: 'width 0.5s ease',
                boxShadow: '0 0 12px rgba(132, 204, 22, 0.45)'
              }} />
            </div>

            {/* Level milestones */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '4px',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              paddingBottom: '4px'
            }}>
              {milestones.map((milestone, index) => {
                const threshold = Number(milestone.threshold || 0);
                const reached = (stats.totalXP || 0) >= threshold;
                return (
                  <div key={milestone.id || milestone.code || index} style={{
                    textAlign: 'center',
                    opacity: reached ? 1 : 0.4,
                    flexShrink: 0,
                    minWidth: isMobile ? '52px' : '82px'
                  }}>
                    <div style={{
                      width: isMobile ? '24px' : '28px',
                      height: isMobile ? '24px' : '28px',
                      borderRadius: '50%',
                      background: reached ? '#4A8C18' : 'rgba(255,255,255,0.1)',
                      margin: '0 auto 4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: isMobile ? '10px' : '12px',
                      fontWeight: '700',
                      color: reached ? '#fff' : 'rgba(255,255,255,0.3)',
                      border: reached ? 'none' : '1px solid rgba(255,255,255,0.1)'
                    }}>
                      {index + 1}
                    </div>
                    <div style={{ fontSize: isMobile ? '9px' : '10px', color: reached ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.3)', fontWeight: '600' }}>
                      {milestone.name || `Tier ${index + 1}`}
                    </div>
                    <div style={{ fontSize: isMobile ? '8px' : '9px', color: 'rgba(255,255,255,0.35)' }}>
                      {threshold} XP
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Upcoming Rewards */}
      {upcomingRewards.length > 0 && (
        <div style={{
          background: 'rgba(255, 193, 7, 0.06)',
          border: '1px solid rgba(255, 193, 7, 0.15)',
          borderRadius: '16px',
          padding: isMobile ? '16px' : '24px',
          marginBottom: isMobile ? '20px' : '32px',
        }}>
          <h3 style={{ margin: '0 0 14px 0', fontSize: isMobile ? '14px' : '16px', color: '#fbbf24', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="tag" color="warning" darkMode={darkMode} size={isMobile ? 16 : 20} />
            Upcoming Rewards
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {upcomingRewards.slice(0, 4).map((reward, idx) => (
              <div key={idx} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 14px',
                background: 'rgba(255,255,255,0.03)',
                borderRadius: '10px',
                flexWrap: 'wrap'
              }}>
                <div style={{ flex: 1, minWidth: '150px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.8)', marginBottom: '2px' }}>{reward.name}</div>
                  <div style={{ fontSize: '12px', color: '#fbbf24' }}>{reward.reward}</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '2px' }}>
                    Unlock at {reward.threshold}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{reward.progress}/{reward.threshold}</div>
                  <div style={{
                    width: '80px',
                    height: '4px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginTop: '4px'
                  }}>
                    <div style={{
                      width: `${reward.progress_percent}%`,
                      height: '100%',
                      background: '#fbbf24',
                      borderRadius: '4px'
                    }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {claimedRewards.length > 0 && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.08)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '16px',
          padding: isMobile ? '16px' : '20px',
          marginBottom: isMobile ? '20px' : '28px',
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: isMobile ? '14px' : '16px', color: '#34d399', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="success" color="success" darkMode={darkMode} size={isMobile ? 16 : 18} />
            Claimed Rewards
          </h3>
          <div style={{ display: 'grid', gap: '8px' }}>
            {claimedRewards.slice(0, 5).map((reward, idx) => (
              <div key={idx} style={{
                padding: '10px 12px',
                borderRadius: '10px',
                background: 'rgba(255,255,255,0.03)',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '10px',
                flexWrap: 'wrap'
              }}>
                <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)', fontWeight: '600' }}>{reward.name}</span>
                <span style={{ fontSize: '12px', color: '#34d399' }}>{reward.reward}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <div className="error" style={{marginBottom: '20px'}}>{error}</div>}

      {loading ? (
        <div className="loading">Loading achievements...</div>
      ) : (
        <>
          {/* Category filter */}
          <div style={{
            display: 'flex',
            gap: isMobile ? '6px' : '10px',
            marginBottom: isMobile ? '20px' : '28px',
            flexWrap: 'wrap',
          }}>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                style={{
                  padding: isMobile ? '8px 12px' : '12px 16px',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: '600',
                  transition: '0.2s',
                  background: selectedCategory === cat 
                    ? '#3d5a20' 
                    : darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  color: selectedCategory === cat ? '#1a1a1a' : (darkMode ? 'rgba(255,255,255,0.6)' : '#4b5563'),
                  boxShadow: selectedCategory === cat ? '0 4px 12px rgba(61, 90, 32, 0.3)' : 'none',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: isMobile ? '4px' : '6px' }}>
                  {CATEGORY_ICONS[cat] ? (
                    <Icon name={CATEGORY_ICONS[cat]} color={selectedCategory === cat ? "brand" : "secondary"} darkMode={darkMode} size={isMobile ? 14 : 16} />
                  ) : (
                    <Icon name="folder" color={selectedCategory === cat ? "brand" : "secondary"} darkMode={darkMode} size={isMobile ? 14 : 16} />
                  )}
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </span>
              </button>
            ))}
          </div>

          {/* Achievements grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: isMobile ? '10px' : '16px',
          }}>
            {filteredTemplates.map(template => (
              <AchievementCard 
                key={template.id}
                template={template}
                achievement={achievementMap[template.id]}
                compact={isMobile}
              />
            ))}
          </div>

          {filteredTemplates.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '60px',
              color: 'rgba(255,255,255,0.4)',
            }}>
              <Icon name="search" color="muted" darkMode={darkMode} size={48} style={{ marginBottom: '16px' }} />
              <p>No achievements found in this category.</p>
            </div>
          )}

          {/* Legend */}
          <div style={{
            marginTop: '40px',
            padding: '20px',
            background: 'var(--bg-overlay-light)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '12px',
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>Achievement Tiers</h3>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              {Object.entries(TIER_COLORS).map(([tier, colors]) => (
                <div key={tier} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: colors.bg,
                  }} />
                  <span style={{ fontSize: '13px', textTransform: 'capitalize', color: 'rgba(255,255,255,0.7)' }}>{tier}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}

export default CompanyAchievementsPage;
